import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, concatMap, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import {
  AddTask,
  AddTaskComplete,
  AddTasks,
  AddTasksComplete,
  ChangeTaskStatus,
  ChangeTaskStatusComplete,
  ChangeTasksStatus,
  ChangeTasksStatusComplete,
  DeleteTask,
  DeleteTaskComplete,
  DeleteTasks,
  DeleteTasksComplete,
  LoadTasks,
  LoadTasksComplete,
  UpdateTask,
  UpdateTaskComplete,
  UpdateTasks,
  UpdateTasksComplete,
  ArchiveTasks,
  ArchiveTasksComplete,
  UnarchiveTasks,
  UnarchiveTasksComplete,
  LoadTasksByIds,
  LoadTasksByIdsComplete,
  LoadTasksByIncidentId,
  LoadTasksByTriggerId,
  LoadTasksByUserIds,
  LoadTasksByInquiryIds,
  LoadDraftTasks,
  LoadDraftTasksComplete,
  RejectTaskComplete,
  RejectTask,
  ApproveTaskComplete,
  ApproveTask,
} from './task.actions'
import { TaskService } from './task.service'
import { TaskStatus, TaskView } from './task.model'
import { Toaster } from '@tokeet-frontend/tv3-platform'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'

@Injectable()
export class TaskEffects {
  @Effect()
  loadTasks$ = this.actions$.pipe(
    ofType(LoadTasks),
    switchMap(({ params }) =>
      this.tasks.all(params).pipe(
        map((tasks) => LoadTasksComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadDraftTasks$ = this.actions$.pipe(
    ofType(LoadDraftTasks),
    switchMap(() =>
      this.tasks.allDrafts().pipe(
        map((tasks) => LoadDraftTasksComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  // load tasks by ids
  loadTasksByIds$ = this.actions$.pipe(
    ofType(LoadTasksByIds),
    switchMap(({ ids }) =>
      this.tasks.all({ pkeys: ids }).pipe(
        map((tasks) => LoadTasksByIdsComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadTasksByIncidentIds$ = this.actions$.pipe(
    ofType(LoadTasksByIncidentId),
    switchMap(({ incidentId }) =>
      this.tasks.all({ incident_id: incidentId }).pipe(
        map((tasks) => LoadTasksByIdsComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadTasksByTrigger$ = this.actions$.pipe(
    ofType(LoadTasksByTriggerId),
    switchMap(({ triggerId }) =>
      this.tasks.all({ trigger_id: triggerId }).pipe(
        map((tasks) => LoadTasksByIdsComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadTasksByUserIds$ = this.actions$.pipe(
    ofType(LoadTasksByUserIds),
    switchMap(({ userIds }) =>
      this.tasks.all({ users: userIds }).pipe(
        map((tasks) => LoadTasksByIdsComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  loadTasksByInquiryIds$ = this.actions$.pipe(
    ofType(LoadTasksByInquiryIds),
    switchMap(({ inquiryIds }) =>
      this.tasks.all({ inquiries: inquiryIds }).pipe(
        map((tasks) => LoadTasksByIdsComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addTask$ = this.actions$.pipe(
    ofType(AddTask),
    concatMap(({ form }) => {
      return this.tasks.add(form).pipe(
        tap(() => this.toast.success($localize`Task created successfully.`)),
        map((task) => AddTaskComplete({ task })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  addTasks$ = this.actions$.pipe(
    ofType(AddTasks),
    switchMap(({ requests }) =>
      this.tasks.addTasks(requests).pipe(
        tap(() => this.toast.success($localize`Tasks created successfully.`)),
        map((tasks: TaskView[]) => AddTasksComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateTask$ = this.actions$.pipe(
    ofType(UpdateTask),
    switchMap(({ id, form }) =>
      this.tasks.update(id, form).pipe(
        tap(() => this.toast.success($localize`Task updated successfully.`)),
        map((task) => UpdateTaskComplete({ update: { id: task.pkey, changes: task } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateTasks$ = this.actions$.pipe(
    ofType(UpdateTasks),
    mergeMap(({ ids, form }) =>
      this.tasks.updateMultiple(ids, form).pipe(
        tap(() => this.toast.success($localize`Tasks updated successfully.`)),
        map((tasks) =>
          UpdateTasksComplete({
            updates: tasks.map((task) => ({ id: task.pkey, changes: task })),
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  rejectTask$ = this.actions$.pipe(
    ofType(RejectTask),
    switchMap(({ taskId }) =>
      this.tasks.delete(taskId).pipe(
        map(() => RejectTaskComplete({ id: taskId })),
        tap(() => this.toast.success($localize`Task rejected successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  approveTask$ = this.actions$.pipe(
    ofType(ApproveTask),
    switchMap(({ taskId }) =>
      this.tasks.update(taskId, { draft: 0, status: TaskStatus.Pending }).pipe(
        map((task) => ApproveTaskComplete({ update: { id: task.pkey, changes: task } })),
        tap(() => this.toast.success($localize`Task approved successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  archiveTasks$ = this.actions$.pipe(
    ofType(ArchiveTasks),
    switchMap(({ ids }) =>
      this.tasks.archiveTasks(ids).pipe(
        tap(() =>
          this.toast.success(
            ids.length > 1 ? $localize`Tasks archived successfully.` : $localize`Task archived successfully.`
          )
        ),
        map((task) => ArchiveTasksComplete({ updates: ids.map((id) => ({ id, changes: { archived: 1 } })) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unarchiveTasks$ = this.actions$.pipe(
    ofType(UnarchiveTasks),
    switchMap(({ ids }) =>
      this.tasks.unarchiveTasks(ids).pipe(
        tap(() =>
          this.toast.success(
            ids.length > 1 ? $localize`Tasks unarchived successfully.` : $localize`Task unarchived successfully.`
          )
        ),
        map((task) => UnarchiveTasksComplete({ updates: ids.map((id) => ({ id, changes: { archived: 0 } })) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteTask$ = this.actions$.pipe(
    ofType(DeleteTask),
    mergeMap(({ id }) =>
      this.tasks.delete(id).pipe(
        map(() => DeleteTaskComplete({ id })),
        tap(() => this.toast.success($localize`Task deleted successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteTasks$ = this.actions$.pipe(
    ofType(DeleteTasks),
    mergeMap(({ ids }) =>
      this.tasks.deleteMultiple(ids).pipe(
        map((task) => DeleteTasksComplete({ ids })),
        tap(() => this.toast.success($localize`Tasks deleted successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  status$ = this.actions$.pipe(
    ofType(ChangeTaskStatus),
    mergeMap(({ status, id }) =>
      this.tasks.changeStatus(status, id).pipe(
        tap(() => this.toast.success($localize`Task updated successfully.`)),
        map(() => ChangeTaskStatusComplete({ update: { id, changes: { status } } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  statuses$ = this.actions$.pipe(
    ofType(ChangeTasksStatus),
    mergeMap(({ status, ids }) =>
      this.tasks.changeStatuses(status, ids).pipe(
        tap(() => this.toast.success($localize`Tasks updated successfully.`)),
        map(() => ChangeTasksStatusComplete({ update: ids.map((id) => ({ id, changes: { status } })) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(private actions$: Actions, private toast: Toaster, private tasks: TaskService) {}
}
